import {
  BrowserAuthError,
  PublicClientApplication,
  createNestablePublicClientApplication,
  type IPublicClientApplication,
  type Configuration,
} from "@azure/msal-browser";
import { envConfig } from "../config";

type DialogEventMessage = { message: string; origin: string | undefined };
type DialogEventError = { error: number };
type DialogEventArg = DialogEventMessage | DialogEventError;

export class MsalAuthService {
  private pca!: IPublicClientApplication;
  private dialogResult: Promise<string> | null = null;
  private initialized = false;
  private useNaa = false;
  private externalAuthHandler?: (scopes: string[]) => Promise<string>;

  async initialize(): Promise<void> {
    if (this.initialized) return;

    const redirectUri = `${window.location.origin}/auth.html`;

    const msalConfig: Configuration = {
      auth: {
        clientId: envConfig.clientId,
        authority: envConfig.authority,
        redirectUri,
        postLogoutRedirectUri: redirectUri,
      },
      cache: {
        cacheLocation: "localStorage",
      },
    };

    // Try Nested App Authentication first (works in Outlook, newer Word/Excel)
    // Fall back to standard MSAL if NAA is not supported
    try {
      this.pca = await createNestablePublicClientApplication(msalConfig);
      this.useNaa = true;
    } catch {
      this.pca = await PublicClientApplication.createPublicClientApplication(msalConfig);
      this.useNaa = false;
    }

    this.initialized = true;
  }

  /**
   * Register an external auth handler for environments where popup and
   * Office Dialog auth are unavailable (e.g., Tauri desktop).
   * The handler receives the requested scopes and must return an access token.
   */
  setExternalAuthHandler(handler: (scopes: string[]) => Promise<string>) {
    this.externalAuthHandler = handler;
  }

  get apiScope(): string[] {
    return [`api://${envConfig.clientId}/.default`];
  }

  get graphScope(): string[] {
    return ["profile", "openid", "user.read"];
  }

  /**
   * Three-tiered token acquisition:
   * 1. Silent
   * 2. Popup
   * 3. Office Dialog API fallback
   */
  async getToken(scopes?: string[]): Promise<string> {
    const targetScopes = scopes ?? this.apiScope;

    if (this.dialogResult) {
      return this.dialogResult;
    }

    // 1. Silent
    try {
      const result = await this.pca.acquireTokenSilent({
        scopes: targetScopes,
      });
      return result.accessToken;
    } catch {
      // Fall through to popup
    }

    // 2. Popup
    try {
      const selectAccount = !this.pca.getActiveAccount();
      const result = await this.pca.acquireTokenPopup({
        scopes: targetScopes,
        prompt: selectAccount ? "select_account" : undefined,
      });
      if (selectAccount) {
        this.pca.setActiveAccount(result.account);
      }
      return result.accessToken;
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "popup_window_error"
      ) {
        // Try Office Dialog API if running inside Office
        if (
          typeof Office !== "undefined" &&
          typeof Office?.context?.ui?.displayDialogAsync === "function"
        ) {
          return this.getTokenWithDialog(targetScopes);
        }
        // Try external auth handler (e.g., Tauri auth window)
        if (this.externalAuthHandler) {
          const token = await this.externalAuthHandler(targetScopes);
          // Refresh account state from shared cache after external auth
          const accounts = this.pca.getAllAccounts();
          if (accounts.length > 0) {
            this.pca.setActiveAccount(accounts[0]);
          }
          return token;
        }
      }
      throw new Error(`Authentication failed: ${error}`);
    }
  }

  private async getTokenWithDialog(_scopes: string[]): Promise<string> {
    this.dialogResult = new Promise((resolve, reject) => {
      const dialogUrl = `${window.location.origin}/dialog.html`;

      Office.context.ui.displayDialogAsync(
        dialogUrl,
        { height: 60, width: 30 },
        (asyncResult) => {
          if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
            this.dialogResult = null;
            reject(new Error("Failed to open auth dialog"));
            return;
          }

          const dialog = asyncResult.value;

          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            (arg: DialogEventArg) => {
              const errorArg = arg as DialogEventError;
              if (errorArg.error === 12006) {
                this.dialogResult = null;
                reject(new Error("Dialog closed"));
              }
            }
          );

          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg: DialogEventArg) => {
              const messageArg = arg as DialogEventMessage;
              const parsed = JSON.parse(messageArg.message);
              dialog.close();

              if (parsed.error) {
                this.dialogResult = null;
                reject(new Error(parsed.error));
              } else {
                resolve(parsed.accessToken);
              }
            }
          );
        }
      );
    });

    return this.dialogResult;
  }

  getActiveAccount() {
    return this.pca?.getActiveAccount() ?? null;
  }
}

export const msalService = new MsalAuthService();
